import React, { useState } from 'react';
import './EpisodeFilters.css';

interface FilterOptions {
  intensities: string[];
  locations: string[];
  dateFrom: string;
  dateTo: string;
}

interface EpisodeFiltersProps {
  onFiltersChange: (filters: FilterOptions) => void;
  totalEpisodes: number;
  filteredCount: number;
}

const EpisodeFilters: React.FC<EpisodeFiltersProps> = ({
  onFiltersChange,
  totalEpisodes,
  filteredCount
}) => {
  const [filters, setFilters] = useState<FilterOptions>({
    intensities: [],
    locations: [],
    dateFrom: '',
    dateTo: ''
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const intensityOptions = [
    { value: 'leve', label: 'Leve', color: '#10b981', emoji: '🟢' },
    { value: 'moderada', label: 'Moderada', color: '#f59e0b', emoji: '🟡' },
    { value: 'fuerte', label: 'Fuerte', color: '#ef4444', emoji: '🔴' }
  ];

  const locationOptions = [
    { value: 'Frente', label: 'Frente', emoji: '👁️' },
    { value: 'Sien', label: 'Sien', emoji: '⚡' },
    { value: 'Parte Superior', label: 'Parte Superior', emoji: '⬆️' },
    { value: 'Nuca', label: 'Nuca', emoji: '🔙' },
    { value: 'Toda la Cabeza', label: 'Toda la Cabeza', emoji: '🤕' }
  ];

  const updateFilters = (newFilters: Partial<FilterOptions>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const toggleIntensity = (intensity: string) => {
    const newIntensities = filters.intensities.includes(intensity)
      ? filters.intensities.filter(i => i !== intensity)
      : [...filters.intensities, intensity];
    
    updateFilters({ intensities: newIntensities });
  };

  const toggleLocation = (location: string) => {
    const newLocations = filters.locations.includes(location)
      ? filters.locations.filter(l => l !== location)
      : [...filters.locations, location];
    
    updateFilters({ locations: newLocations });
  };

  const clearAllFilters = () => {
    const emptyFilters = {
      intensities: [],
      locations: [],
      dateFrom: '',
      dateTo: ''
    };
    setFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  const hasActiveFilters = filters.intensities.length > 0 || 
                          filters.locations.length > 0 || 
                          filters.dateFrom || 
                          filters.dateTo;

  const getFilterSummary = () => {
    const parts = [];
    if (filters.intensities.length > 0) {
      parts.push(`${filters.intensities.length} intensidad${filters.intensities.length > 1 ? 'es' : ''}`);
    }
    if (filters.locations.length > 0) {
      parts.push(`${filters.locations.length} ubicación${filters.locations.length > 1 ? 'es' : ''}`);
    }
    if (filters.dateFrom || filters.dateTo) {
      parts.push('rango de fechas');
    }
    return parts.join(', ');
  };

  return (
    <div className="episode-filters">
      <div className="filters-header">
        <div className="filters-info">
          <h4 className="filters-title">
            🔍 Filtros de episodios
            {hasActiveFilters && (
              <span className="filter-badge">
                {getFilterSummary()}
              </span>
            )}
          </h4>
          <div className="episode-count">
            Mostrando {filteredCount} de {totalEpisodes} episodios
            {hasActiveFilters && (
              <button 
                className="clear-filters-btn"
                onClick={clearAllFilters}
                title="Limpiar filtros"
              >
                ✕ Limpiar
              </button>
            )}
          </div>
        </div>
        
        <button 
          className={`toggle-filters-btn ${isExpanded ? 'expanded' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? 'Ocultar filtros' : 'Mostrar filtros'}
        >
          {isExpanded ? '▲' : '▼'}
        </button>
      </div>

      {isExpanded && (
        <div className="filters-content">
          {/* Filtros de Intensidad */}
          <div className="filter-group">
            <label className="filter-label">
              📊 Intensidad
            </label>
            <div className="filter-pills">
              {intensityOptions.map(option => (
                <button
                  key={option.value}
                  className={`filter-pill intensity-pill ${
                    filters.intensities.includes(option.value) ? 'selected' : ''
                  }`}
                  style={filters.intensities.includes(option.value) ? {
                    backgroundColor: option.color,
                    color: 'white',
                    borderColor: option.color
                  } : undefined}
                  onClick={() => toggleIntensity(option.value)}
                >
                  <span className="pill-emoji">{option.emoji}</span>
                  <span className="pill-text">{option.label}</span>
                  {filters.intensities.includes(option.value) && (
                    <span className="pill-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filtros de Ubicación */}
          <div className="filter-group">
            <label className="filter-label">
              📍 Ubicación
            </label>
            <div className="filter-pills">
              {locationOptions.map(option => (
                <button
                  key={option.value}
                  className={`filter-pill location-pill ${
                    filters.locations.includes(option.value) ? 'selected' : ''
                  }`}
                  onClick={() => toggleLocation(option.value)}
                >
                  <span className="pill-emoji">{option.emoji}</span>
                  <span className="pill-text">{option.label}</span>
                  {filters.locations.includes(option.value) && (
                    <span className="pill-check">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filtros de Fecha */}
          <div className="filter-group">
            <label className="filter-label">
              📅 Rango de fechas
            </label>
            <div className="date-filters">
              <div className="date-input-group">
                <label htmlFor="dateFrom" className="date-label">Desde:</label>
                <input
                  type="date"
                  id="dateFrom"
                  className="date-input"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilters({ dateFrom: e.target.value })}
                />
              </div>
              <div className="date-separator">—</div>
              <div className="date-input-group">
                <label htmlFor="dateTo" className="date-label">Hasta:</label>
                <input
                  type="date"
                  id="dateTo"
                  className="date-input"
                  value={filters.dateTo}
                  onChange={(e) => updateFilters({ dateTo: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Botón de limpiar filtros (versión extendida) */}
          {hasActiveFilters && (
            <div className="filter-actions">
              <button 
                className="clear-all-btn"
                onClick={clearAllFilters}
              >
                🗑️ Limpiar todos los filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EpisodeFilters;